import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Clock, ShieldOff, Info, Scale, Send, Loader2 } from 'lucide-react';

interface AccountStatus {
  account_state: 'good' | 'limited' | 'at_risk';
  is_suspended: boolean;
  suspended_until: string | null;
  active_actions: ActiveAction[];
}

interface ActiveAction {
  id: string;
  action_type: 'warning' | 'violation' | 'restriction' | 'temp_suspension' | 'ban';
  reason: string;
  sub_type: string | null;
  expires_at: string | null;
  created_at: string;
}

interface Appeal {
  id: string;
  action_id: string;
  status: 'pending' | 'accepted' | 'rejected';
  reason: string;
  admin_response: string | null;
  created_at: string;
}

const ACTION_TYPE_LABELS: Record<string, string> = {
  warning: 'Avertissement',
  violation: 'Violation',
  restriction: 'Restriction',
  temp_suspension: 'Suspension temporaire',
};

const RESTRICTION_LABELS: Record<string, string> = {
  no_messages: 'Envoi de messages désactivé',
  no_images: 'Partage d\'images désactivé',
  no_reactions: 'Réactions désactivées',
  no_media: 'Partage de médias désactivé',
};

const STATE_CONFIG = {
  good:     { label: 'Tout va bien',  color: 'text-success',  bg: 'bg-success/10 border-success/30',  Icon: CheckCircle },
  limited:  { label: 'Limité',        color: 'text-warning',  bg: 'bg-warning/10 border-warning/30',  Icon: AlertTriangle },
  at_risk:  { label: 'À risque',      color: 'text-error',    bg: 'bg-error/10 border-error/30',       Icon: XCircle },
};

const AccountStatusTab: React.FC = () => {
  const [status, setStatus] = useState<AccountStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Appeals state
  const [appeals, setAppeals] = useState<Record<string, Appeal>>({});
  const [appealingAction, setAppealingAction] = useState<string | null>(null);
  const [appealTexts, setAppealTexts] = useState<Record<string, string>>({});
  const [submittingAppeal, setSubmittingAppeal] = useState<string | null>(null);
  const [appealError, setAppealError] = useState<string | null>(null);

  const apiBase = import.meta.env.VITE_API_URL || '/api';
  const authHeaders = { Authorization: `Bearer ${localStorage.getItem('access_token')}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${apiBase}/me/account-status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        if (!res.ok) throw new Error('Impossible de charger le statut.');
        const data = await res.json();
        setStatus(data);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();

    const fetchAppeals = async () => {
      try {
        const res = await fetch(`${apiBase}/me/appeals`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        if (!res.ok) return;
        const data: Appeal[] = await res.json();
        const map: Record<string, Appeal> = {};
        for (const a of data) map[a.action_id] = a;
        setAppeals(map);
      } catch {}
    };
    fetchAppeals();
  }, []);

  const handleSubmitAppeal = async (actionId: string) => {
    const reason = (appealTexts[actionId] || '').trim();
    if (!reason) return;
    setSubmittingAppeal(actionId);
    setAppealError(null);
    try {
      const res = await fetch(`${apiBase}/me/appeals`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ action_id: actionId, reason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      const aRes = await fetch(`${apiBase}/me/appeals`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      if (aRes.ok) {
        const aData: Appeal[] = await aRes.json();
        const map: Record<string, Appeal> = {};
        for (const a of aData) map[a.action_id] = a;
        setAppeals(map);
      }
      setAppealingAction(null);
    } catch (e: any) {
      setAppealError(e.message);
    } finally {
      setSubmittingAppeal(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-6 h-6 text-accent-color animate-spin" />
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-xl bg-error/10 border border-error/30 text-error text-sm">
        <XCircle className="w-4 h-4 flex-shrink-0" /> {error || 'Erreur inconnue'}
      </div>
    );
  }

  const cfg = STATE_CONFIG[status.account_state] ?? STATE_CONFIG.good;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-text-primary">Statut du compte</h2>
        <p className="text-xs text-text-muted mt-0.5">Consultez l'état de votre compte et les sanctions actives.</p>
      </div>

      {/* State badge */}
      <div className={`border rounded-xl p-4 flex items-center gap-3 ${cfg.bg}`}>
        <cfg.Icon className={`h-5 w-5 flex-shrink-0 ${cfg.color}`} />
        <div>
          <p className={`text-sm font-bold ${cfg.color}`}>{cfg.label}</p>
          <p className="text-xs text-text-secondary mt-0.5">
            {status.account_state === 'good'
              ? 'Votre compte respecte nos règles de communauté. Continuez comme ça !'
              : status.account_state === 'limited'
              ? 'Votre compte a des restrictions actives en raison d\'actions de modération.'
              : 'Votre compte est à risque suite à de multiples violations.'}
          </p>
        </div>
      </div>

      {/* Suspension notice */}
      {status.is_suspended && status.suspended_until && (
        <div className="border border-error/40 bg-error/10 rounded-xl p-4 flex items-center gap-3">
          <ShieldOff className="h-4 w-4 text-error flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-error">Compte suspendu</p>
            <p className="text-xs text-text-secondary mt-0.5">
              Suspension levée le{' '}
              <span className="text-text-primary font-medium">
                {new Date(status.suspended_until).toLocaleDateString('fr-FR', {
                  day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Active actions */}
      {status.active_actions.length > 0 ? (
        <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5">
          <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide mb-3 flex items-center gap-2">
            <ShieldOff className="w-4 h-4 text-accent-color" />
            Actions en cours ({status.active_actions.length})
          </h3>
          <div className="space-y-2">
            {status.active_actions.map(action => {
              const appeal = appeals[action.id];
              const isAppealOpen = appealingAction === action.id;
              return (
              <div
                key={action.id}
                className="bg-bg-tertiary/50 border border-border-color rounded-lg p-3 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${
                    action.action_type === 'warning'      ? 'bg-warning/15 text-warning' :
                    action.action_type === 'violation'    ? 'bg-orange-500/15 text-orange-400' :
                    action.action_type === 'restriction'  ? 'bg-error/15 text-error' :
                    action.action_type === 'ban'          ? 'bg-error/20 text-error font-black' :
                    'bg-purple-500/15 text-purple-400'
                  }`}>
                    {ACTION_TYPE_LABELS[action.action_type] ?? action.action_type}
                  </span>

                  {action.expires_at ? (
                    <span className="text-[10px] text-text-muted flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Expire le {new Date(action.expires_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  ) : (
                    <span className="text-[10px] text-text-muted">Permanent</span>
                  )}
                </div>

                {action.sub_type && (
                  <p className="text-[10px] text-text-muted">
                    {RESTRICTION_LABELS[action.sub_type] ?? action.sub_type}
                  </p>
                )}

                <p className="text-xs text-text-primary">{action.reason}</p>

                <p className="text-[10px] text-text-muted">
                  Appliqué le {new Date(action.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>

                {/* Appeal section */}
                {appeal ? (
                  <div className={`rounded-lg border p-2.5 text-xs space-y-1 ${
                    appeal.status === 'pending'  ? 'bg-warning/10 border-warning/30' :
                    appeal.status === 'accepted' ? 'bg-success/10 border-success/30' :
                                                    'bg-error/10 border-error/30'
                  }`}>
                    <div className="flex items-center gap-1.5 font-semibold">
                      <Scale className={`h-3 w-3 ${
                        appeal.status === 'pending'  ? 'text-warning' :
                        appeal.status === 'accepted' ? 'text-success' : 'text-error'
                      }`} />
                      <span className={`text-[10px] ${
                        appeal.status === 'pending'  ? 'text-warning' :
                        appeal.status === 'accepted' ? 'text-success' : 'text-error'
                      }`}>
                        Contestation {appeal.status === 'pending' ? 'en cours d\'examen' : appeal.status === 'accepted' ? 'acceptée' : 'refusée'}
                      </span>
                    </div>
                    {appeal.admin_response && (
                      <p className="text-text-secondary italic text-[10px]">"{appeal.admin_response}"</p>
                    )}
                  </div>
                ) : isAppealOpen ? (
                  <div className="space-y-2 border-t border-border-color pt-2">
                    <p className="text-[10px] font-bold text-text-secondary flex items-center gap-1.5 uppercase tracking-wide">
                      <Scale className="h-3 w-3" /> Contester cette sanction
                    </p>
                    <textarea
                      rows={3}
                      value={appealTexts[action.id] || ''}
                      onChange={e => setAppealTexts(prev => ({ ...prev, [action.id]: e.target.value }))}
                      placeholder="Expliquez pourquoi vous contestez cette sanction..."
                      className="settings-input resize-none !h-20"
                    />
                    {appealError && (
                      <p className="text-[10px] text-error">{appealError}</p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setAppealingAction(null)}
                        className="flex-1 px-3 py-1.5 rounded-lg bg-bg-tertiary border border-border-color text-xs text-text-secondary hover:text-text-primary transition-all"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={() => handleSubmitAppeal(action.id)}
                        disabled={!appealTexts[action.id]?.trim() || submittingAppeal === action.id}
                        className="flex-1 px-3 py-1.5 rounded-lg bg-accent-color text-black text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-40 hover:bg-accent-hover transition-all"
                      >
                        {submittingAppeal === action.id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Send className="h-3 w-3" />
                        }
                        Envoyer
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => { setAppealingAction(action.id); setAppealError(null); }}
                    className="flex items-center gap-1.5 text-[10px] text-text-muted hover:text-accent-color transition-colors"
                  >
                    <Scale className="h-3 w-3" /> Contester cette sanction
                  </button>
                )}
              </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-4 flex items-center gap-3 text-text-secondary">
          <Info className="h-4 w-4 flex-shrink-0 text-accent-color" />
          <p className="text-xs">Aucune action de modération active sur votre compte.</p>
        </div>
      )}

      <p className="text-[10px] text-text-muted">
        Pour toute question supplémentaire, contactez l'équipe de modération à{' '}
        <a href="mailto:moderation@erozion.net" className="text-accent-color hover:underline">
          moderation@erozion.net
        </a>
        .
      </p>
    </div>
  );
};

export default AccountStatusTab;
